/**
 * Whisper Block — Thin Loader
 *
 * Finds the whisper container, loads the Svelte bundle from CDN,
 * and mounts WhisperApp. Bridges whisper:open-chat events to the
 * gallery-wall DiscoverApp opened in a side-drawer modal (lazy-loads
 * gallery-wall.js from the chat-agent CDN on first use).
 */
(function () {
  'use strict';

  // Idempotency guard — this script may be referenced from multiple sections
  // (cart-drawer snippet ships it globally; PDPs may reference it too).
  // Without the guard we'd register two whisper:open-chat listeners and
  // open two drawers per click.
  if (window.__whisperBlockLoaded) return;
  window.__whisperBlockLoaded = true;

  // ── Signal Collector ──────────────────────────────────────────────────
  // Accumulates behavioral signals in sessionStorage for the orchestrator.
  // Piggybacks on existing theme DOM events — no new tracking.

  const SIGNAL_STORAGE_KEY = 'whisper:signals';

  function getSignals() {
    try { return JSON.parse(sessionStorage.getItem(SIGNAL_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  }

  function saveSignals(s) {
    sessionStorage.setItem(SIGNAL_STORAGE_KEY, JSON.stringify(s));
  }

  function incrementSignal(key) {
    const s = getSignals();
    s[key] = (s[key] || 0) + 1;
    saveSignals(s);
  }

  // Listen to existing theme events
  document.addEventListener('size-impact:changed', () => incrementSignal('sizeChanges'));
  document.addEventListener('style-picker:preset-selected', () => incrementSignal('styleChanges'));
  document.addEventListener('style-picker:custom-applied', () => incrementSignal('styleChanges'));

  // Cart dwell timer
  let cartDwellStart = null;

  function startCartDwell() {
    if (!cartDwellStart) cartDwellStart = Date.now();
  }

  function getCartDwellMs() {
    return cartDwellStart ? Date.now() - cartDwellStart : 0;
  }

  /**
   * Build the full signal snapshot for the whisper API request.
   * Called by the Svelte WhisperBlock component at fetch time.
   */
  function buildSignalSnapshot() {
    const counters = getSignals();

    let recentlyViewed = [];
    try {
      recentlyViewed = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
    } catch {}

    return {
      sizeChanges: counters.sizeChanges || 0,
      styleChanges: counters.styleChanges || 0,
      dwellMs: getCartDwellMs(),
      recentlyViewed,
      hasRoomPhoto: !!sessionStorage.getItem('whisper:room-photo'),
      isLoggedIn: false, // TODO Phase 4: check Rivo SDK
      activeDiscount: false, // TODO Phase 3: check cart discount state
      maxQuantity: 1, // TODO Phase 4: read from cart DOM
    };
  }

  // Expose signal snapshot builder globally for whisper-service.js to call
  window.__whisperBuildSignals = buildSignalSnapshot;

  // The advisor SPA is served via the `advisor` app proxy (see
  // shop-chat-agent/shopify.app.advisor.toml: prefix=a, subpath=advisor).
  // /a/advisor/*.js + /a/advisor/*.css forward to the gallery-wall handler
  // on the deployed app — same Svelte bundle, but a separate proxy with
  // its own deploy cadence so the storefront drawer can load the latest
  // advisor build independently of the gallery-wall builder.
  const CDN_BASE_URL = 'https://shop-chat-agent.iamfy.tech/advisor';
  const APP_PROXY_PATH = document.querySelector('meta[name="advisor-app-url"]')?.content || '/a/advisor';

  let bundleLoaded = false;
  let whisperInstance = null;

  async function loadWhisperBundle(appUrl) {
    if (bundleLoaded) return;

    // Determine base URL: try app proxy first (works in dev), fall back to CDN
    const baseUrl = appUrl || APP_PROXY_PATH;

    let cacheBust = '';
    try {
      const vRes = await fetch(baseUrl + '/gallery-wall-version.json');
      if (vRes.ok) {
        const v = await vRes.json();
        cacheBust = '?v=' + (v.version || Date.now());
      }
    } catch { cacheBust = '?v=' + Date.now(); }

    const cssReady = new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = baseUrl + '/whisper-bundle.css' + cacheBust;
      link.onload = resolve;
      link.onerror = resolve;
      document.head.appendChild(link);
    });

    const jsReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = baseUrl + '/whisper-bundle.js' + cacheBust;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load whisper bundle'));
      document.head.appendChild(script);
    });

    await Promise.all([cssReady, jsReady]);
    bundleLoaded = true;
  }

  async function mountWhisper(container) {
    try {
      const appUrl = container.dataset.appUrl || APP_PROXY_PATH;
      await loadWhisperBundle(appUrl);

      if (!window.WhisperApp) {
        console.error('[Whisper] Bundle loaded but WhisperApp not found');
        return;
      }

      whisperInstance = window.WhisperApp({
        target: container,
        props: {
          appUrl,
          placement: container.dataset.placement || 'pdp',
          productHandle: container.dataset.productHandle || '',
          market: container.dataset.market || 'gb',
          artProduct: container.dataset.artProduct || 'art-print',
        },
      });
    } catch (err) {
      console.error('[Whisper] Failed to mount:', err);
    }
  }

  // ── Cart whisper mounting ───────────────────────────────────────────────

  let cartWhisperInstance = null;

  async function getCartHandles() {
    // Try DOM first (fastest, works when cart drawer is open)
    try {
      const cartItems = document.querySelectorAll('[data-cart-item-handle]');
      if (cartItems.length > 0) {
        return Array.from(cartItems).map(el => el.dataset.cartItemHandle).filter(Boolean);
      }
    } catch {}

    // Fallback: fetch from /cart.js
    try {
      const res = await fetch('/cart.js');
      if (res.ok) {
        const cart = await res.json();
        return (cart.items || []).map(item => item.handle).filter(Boolean);
      }
    } catch {}

    return [];
  }

  async function mountCartWhisper(container) {
    try {
      const appUrl = container.dataset.appUrl || APP_PROXY_PATH;
      await loadWhisperBundle(appUrl);

      if (!window.WhisperApp) {
        console.error('[Whisper] Bundle loaded but WhisperApp not found');
        return;
      }

      // Destroy previous instance
      if (cartWhisperInstance) {
        try { cartWhisperInstance.destroy(); } catch {}
        container.innerHTML = '';
      }

      const handles = await getCartHandles();
      if (handles.length === 0) {
        container.innerHTML = '';
        return;
      }

      // Sort handles for stable session key
      const sortedHandles = [...handles].sort().join(',');

      cartWhisperInstance = window.WhisperApp({
        target: container,
        props: {
          appUrl,
          placement: 'cart',
          productHandle: sortedHandles,
          market: container.dataset.market || 'gb',
          artProduct: container.dataset.artProduct || 'art-print',
        },
      });
    } catch (err) {
      console.error('[Whisper] Failed to mount cart whisper:', err);
    }
  }

  function initCartWhisper() {
    startCartDwell();
    const container = document.querySelector('.whisper-cart-container');
    if (!container) return;

    // Gated by PostHog feature flag (whisper-cart-enabled class on <html>)
    if (!document.documentElement.classList.contains('whisper-cart-enabled')) return;

    // Mount immediately if cart has items
    mountCartWhisper(container);

    // Watch for cart changes (items added/removed)
    document.addEventListener('cart:refresh', () => {
      setTimeout(() => mountCartWhisper(container), 300);
    });

    // Watch for the container becoming visible (cart drawer open)
    const observer = new MutationObserver(() => {
      if (container.offsetParent !== null && !cartWhisperInstance) {
        mountCartWhisper(container);
      }
    });
    observer.observe(container.parentElement || document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'open'],
    });
  }

  // ── DiscoverDrawer — plain JS/CSS drawer shell ───────────────────────
  //
  // Drawer DOM + CSS is created here. DiscoverApp is loaded from the main
  // gallery-wall ESM bundle (lazy) and mounted into the drawer's #dd-gw-app.
  // Mirrors shop-chat-agent/extensions/chat-bubble/assets/whisper-block.js
  // so the PDP advisor entry points open the same SPA the whisper widget does.

  let drawerState = 'closed'; // 'closed' | 'open'
  let drawerEl = null;
  let backdropEl = null;
  let discoverMounted = false;
  // ab12: track the previous mount's intent classification so we can force
  // a fresh mount when the user switches between skills (e.g. PDP frame/size
  // reco → gallery-wall curation). Without this the SPA reuses the prior
  // conversation and shows stale frame chips when the user clearly asked
  // for a different thing. Intent buckets are coarse — 'pdp' (focusedPiece
  // present) vs 'gw' (no focusedPiece, free-form curation).
  let lastIntentBucket = null;
  // Single promise shared between concurrent callers — prevents the race
  // where a second openDiscoverDrawer() awaits `undefined` and mounts before
  // the bundle has finished loading.
  let esmBundlePromise = null;

  // Progressive disclosure widths. The drawer opens narrow with the SPA in
  // compact (chat-only) mode. When the SPA dispatches `discover:enter-split`
  // (proposals + frames ready), the drawer animates to the split width and
  // tells the SPA to flip out of compact so the wall preview column renders
  // alongside the chat. Below 480 px viewport the drawer naturally fills
  // the screen via max-width:100vw.
  const DRAWER_WIDTH_NARROW = 480;
  // Just above the SPA's @media (min-width: 768px) breakpoint, with room
  // for the SPA's 400 px chat panel + ~580 px wall preview.
  const DRAWER_WIDTH_SPLIT = 1024;

  function injectDrawerStyles() {
    if (document.getElementById('discover-drawer-css')) return;
    const style = document.createElement('style');
    style.id = 'discover-drawer-css';
    style.textContent = `
      .dd-backdrop {
        position: fixed; inset: 0; z-index: 9998;
        background: rgba(0,0,0,0); transition: background 0.3s ease;
      }
      .dd-backdrop--visible { background: rgba(20, 20, 20, 0.55); }
      .dd-drawer {
        position: fixed; top: 0; right: 0; bottom: 0;
        width: ${DRAWER_WIDTH_NARROW}px; max-width: 100%;
        z-index: 9999; background: #e8e3da;
        display: flex; flex-direction: column;
        box-shadow: -4px 0 24px rgba(0,0,0,0.15);
        /* transform creates a containing block for position:fixed children
           inside the SPA, so the SPA chrome is constrained to the drawer
           width instead of the full viewport. */
        transform: translateX(0);
        transition: transform 0.3s cubic-bezier(0.4,0,0.2,1),
                    width   0.32s cubic-bezier(0.2,0.8,0.2,1);
      }
      .dd-drawer--split { width: min(${DRAWER_WIDTH_SPLIT}px, 100%); }
      .dd-drawer--opening { transform: translateX(100%); animation: ddSlideIn 0.3s cubic-bezier(0.4,0,0.2,1) forwards; }
      .dd-drawer--closing { animation: ddSlideOut 0.3s cubic-bezier(0.4,0,0.2,1) forwards; }
      @keyframes ddSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      @keyframes ddSlideOut { from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } }
      .dd-close {
        position: absolute; top: 12px; right: 12px; z-index: 10001;
        width: 36px; height: 36px; border-radius: 50%; border: none;
        background: rgba(255,255,255,0.85); backdrop-filter: blur(8px);
        cursor: pointer; display: flex; align-items: center; justify-content: center;
        color: #333; transition: background 0.15s;
      }
      .dd-close:hover { background: rgba(255,255,255,1); }
      /* 328a: anchor to the LEFT edge so we don't collide with chips
         the SPA renders in its own top-right (e.g. the "Skip all →
         curate anyway" pill). The close X stays at the right. */
      .dd-open-full {
        position: absolute; top: 18px; left: 18px; z-index: 10001;
        font: 600 12px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        color: #333; background: rgba(255,255,255,0.85); backdrop-filter: blur(8px);
        padding: 7px 12px; border-radius: 99px;
        border: 0; cursor: pointer;
        transition: background 0.15s, color 0.15s;
      }
      .dd-open-full:hover { background: rgba(255,255,255,1); color: #14110d; }
      /* The button toggles the wall-preview column in and out, so it stays
         visible in both narrow and split states. */
      .dd-content { flex: 1; overflow: hidden; width: 100%; height: 100%; }
      .dd-loading { display: flex; align-items: center; justify-content: center; height: 100%; gap: 8px; }
      .dd-dot { width: 8px; height: 8px; border-radius: 50%; background: #999; animation: ddPulse 1.2s ease-in-out infinite; }
      .dd-dot:nth-child(2) { animation-delay: 0.2s; }
      .dd-dot:nth-child(3) { animation-delay: 0.4s; }
      @keyframes ddPulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1); } }

      /* The SPA renders its own mobile-header (logo + "Art Advisor" chip +
         action buttons) with position:fixed; left:0; right:0 — designed to
         span the viewport when the SPA is the whole page. Inside our drawer
         it escapes to the viewport-left and visually disconnects from the
         drawer content. Hide it: the drawer already has a close button, and
         the SPA's other actions (share, ATC) live elsewhere in compact mode. */
      .dd-drawer .discover__mobile-header { display: none !important; }
      /* The mobile tabs (Chat / Shortlist / Curations) render unstyled on a
         desktop viewport because the SPA's CSS for them is gated by
         @media (max-width: 767px). Inside our drawer the SPA is in
         force-compact mode but the actual viewport is desktop, so those
         media queries don't fire. Hide the tabs — the chat panel is the
         primary surface in compact mode; shortlist + curations remain
         reachable from the SPA's split state and the full-screen view. */
      .dd-drawer .discover__mobile-tabs { display: none !important; }
      /* Hide the SPA's first-run "Tap + to save art you love" floating hint —
         it sits at the top of the thread on every drawer open until the user
         saves their first piece, which reads as drawer-chrome copy and
         confuses the conversation start. Drawer can ship its own onboarding
         affordance later if needed. */
      .dd-drawer .thread__save-hint { display: none !important; }

      /* BriefPanel (the ask-mode question form) ships position:fixed; left:400px
         designed for the full /a/advisor layout where the 400px chat panel sits
         on the left and the form covers everything to the right of it.
         Inside the drawer the drawer's transform creates a containing block, so
         position:fixed resolves *relative to the drawer*. left:400px then pushes
         the form almost entirely off the right side (out of the drawer entirely).
         Pin it to the drawer's left edge so it overlays the chat panel — which
         is exactly what the user sees in embedded mode. */
      .dd-drawer .br { left: 0 !important; }

      /* Reclaim the 48px of top space the SPA's CSS reserves for that header. */
      .dd-drawer .discover__work-content,
      .dd-drawer .discover__panel,
      .dd-drawer .discover__preview { padding-top: 0 !important; }

      /* ── Narrow (chat-only) state ─────────────────────────────────────
         The SPA always renders both the chat panel and the wall-preview
         column. In the drawer's narrow state we hide the preview entirely
         and force the panel to fill the available width — the user only
         sees chat until proposals trigger the split expansion. */
      .dd-drawer:not(.dd-drawer--split) .discover__preview {
        display: none !important;
      }
      .dd-drawer:not(.dd-drawer--split) .discover__panel {
        flex: 1 1 auto !important;
        max-width: 100% !important;
        border-right: 0 !important;
      }

      /* ── Split state ─────────────────────────────────────────────────
         Once the SPA dispatches discover:enter-split and the drawer
         animates to its wider size, the SPA own desktop layout (media
         min-width 768px) takes over: chat panel ~400 px on the left,
         wall preview filling the rest. We just stop overriding it. */
    `;
    document.head.appendChild(style);
  }

  function createDrawerDOM() {
    injectDrawerStyles();

    backdropEl = document.createElement('div');
    backdropEl.className = 'dd-backdrop';
    backdropEl.addEventListener('click', closeDiscoverDrawer);
    document.body.appendChild(backdropEl);

    drawerEl = document.createElement('div');
    drawerEl.className = 'dd-drawer';
    drawerEl.setAttribute('role', 'dialog');
    drawerEl.setAttribute('aria-modal', 'true');
    drawerEl.setAttribute('aria-label', 'Art Advisor');

    // This button shows / hides the second column (the wall preview).
    // Showing it widens the drawer to its split size so the SPA's desktop
    // chat-+-preview layout has room; hiding it collapses back to the
    // narrow chat-only size.
    const openFullBtn = document.createElement('button');
    openFullBtn.type = 'button';
    openFullBtn.className = 'dd-open-full';
    openFullBtn.textContent = 'Show wall →';
    openFullBtn.setAttribute('aria-label', 'Show the wall preview');
    openFullBtn.addEventListener('click', togglePreviewColumn);
    drawerEl.appendChild(openFullBtn);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'dd-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>';
    closeBtn.addEventListener('click', closeDiscoverDrawer);
    drawerEl.appendChild(closeBtn);

    const content = document.createElement('div');
    content.className = 'dd-content';

    const loading = document.createElement('div');
    loading.className = 'dd-loading';
    loading.id = 'dd-loading';
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('div');
      dot.className = 'dd-dot';
      loading.appendChild(dot);
    }
    content.appendChild(loading);

    const gwApp = document.createElement('div');
    gwApp.id = 'dd-gw-app';
    gwApp.style.width = '100%';
    gwApp.style.height = '100%';
    gwApp.style.display = 'none';
    content.appendChild(gwApp);

    drawerEl.appendChild(content);
    document.body.appendChild(drawerEl);

    document.addEventListener('keydown', handleDrawerKeydown);
    document.addEventListener('discover:enter-split', enterSplit);
  }

  // The SPA dispatches `discover:enter-split` once `wallVisible && hasFrames`
  // (proposals/wall ready). Animate the drawer to its split width and tell
  // the SPA to flip out of compact so its desktop chat-+-preview layout
  // renders inside the now-wide drawer.
  // Keep the toggle button's label in sync with whether the wall-preview
  // column is currently showing.
  function syncPreviewToggleLabel() {
    const btn = drawerEl?.querySelector('.dd-open-full');
    if (!btn) return;
    if (drawerEl.classList.contains('dd-drawer--split')) {
      btn.textContent = '← Hide wall';
      btn.setAttribute('aria-label', 'Hide the wall preview');
    } else {
      btn.textContent = 'Show wall →';
      btn.setAttribute('aria-label', 'Show the wall preview');
    }
  }

  function enterSplit() {
    if (!drawerEl || drawerState === 'closed') return;
    if (drawerEl.classList.contains('dd-drawer--split')) return;
    drawerEl.classList.add('dd-drawer--split');
    document.dispatchEvent(new CustomEvent('discover:set-compact', { detail: false }));
    syncPreviewToggleLabel();
  }

  function exitSplit() {
    if (!drawerEl) return;
    drawerEl.classList.remove('dd-drawer--split');
    document.dispatchEvent(new CustomEvent('discover:set-compact', { detail: true }));
    syncPreviewToggleLabel();
  }

  // Show / hide the second column (the wall preview). Showing it widens the
  // drawer to its split size so the SPA's desktop chat-+-preview layout has
  // room; hiding it collapses back to the narrow chat-only size.
  function togglePreviewColumn() {
    if (!drawerEl) return;
    if (drawerEl.classList.contains('dd-drawer--split')) {
      exitSplit();
    } else {
      enterSplit();
    }
  }

  function handleDrawerKeydown(e) {
    if (e.key === 'Escape' && drawerState !== 'closed') {
      closeDiscoverDrawer();
    }
  }

  function getAppBaseUrl() {
    const pdpContainer = document.querySelector('.whisper-block-container');
    return pdpContainer?.dataset.appUrl || APP_PROXY_PATH;
  }

  async function loadGalleryWallESM() {
    if (window.initGalleryWall) return;
    if (esmBundlePromise) return esmBundlePromise;

    esmBundlePromise = (async () => {
      const baseUrl = getAppBaseUrl();

      // Always use a non-empty cacheBust. If the app proxy /version endpoint
      // returns ok, key off its version (stable across users — re-fetched
      // only on deploy). Otherwise fall back to Date.now() — without busting,
      // the browser serves a stale gallery-wall.js from its disk cache, and
      // that cached entry-file references the previous build's hashed
      // discover-entry chunk by name. Every user then keeps loading whichever
      // build was current when they first visited.
      let cacheBust = '?v=' + Date.now();
      try {
        const vRes = await fetch(baseUrl + '/version');
        if (vRes.ok) {
          const v = await vRes.json();
          if (v && v.version) cacheBust = '?v=' + v.version;
        }
      } catch { /* keep Date.now() fallback */ }

      // Await the entry stylesheet load before importing JS — without this
      // the SPA mounts and renders before its base styles arrive, producing
      // a brief unstyled-content flash where chrome elements are positioned
      // by user-agent defaults instead of by the SPA's grid/flex layout.
      // Vite-injected chunked stylesheets (discover-entry, etc.) are
      // managed by the bundle's preload helper.
      await new Promise((resolve) => {
        const existing = document.querySelector('link[href*="gallery-wall.css"]');
        if (existing) {
          // If it was injected previously and is still loading, wait for it;
          // otherwise resolve immediately.
          if (existing.sheet) return resolve();
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', resolve, { once: true });
          return;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = baseUrl + '/gallery-wall.css' + cacheBust;
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true }); // don't block on CSS failure
        document.head.appendChild(link);
      });

      await import(baseUrl + '/gallery-wall.js' + cacheBust);
    })();

    // Reset the promise on failure so retries can re-attempt the load.
    esmBundlePromise.catch(() => { esmBundlePromise = null; });

    return esmBundlePromise;
  }

  /**
   * Open the drawer + mount the SPA. Optionally awaits async preparation
   * (e.g. uploading a photo to S3) before SPA mount, so the SPA reads the
   * fully-resolved handoff exactly once.
   *
   * @param {Function} [prepareHandoff] async () => void — runs in parallel
   *   with bundle load. Should mutate sessionStorage['whisper:handoff'] to
   *   its final shape before resolving.
   */
  async function openDiscoverDrawer(prepareHandoff, startSplit = false) {
    if (drawerState !== 'closed') return;

    if (!drawerEl) createDrawerDOM();
    drawerEl.style.display = '';

    document.body.style.overflow = 'hidden';

    drawerEl.className = 'dd-drawer dd-drawer--opening';
    backdropEl.style.display = '';
    // d05f: closeDiscoverDrawer sets pointer-events: none on the backdrop
    // so the page stays clickable during the close animation. Without
    // resetting it on reopen, the second open shows a visible-but-dead
    // backdrop — clicks pass through and don't dismiss the drawer.
    backdropEl.style.pointerEvents = '';
    requestAnimationFrame(() => backdropEl.classList.add('dd-backdrop--visible'));

    drawerState = 'open';

    // Personalise-on-PDP opens straight into the 2-col (chat + live preview)
    // layout — the drawer expands to its split width (1024px) immediately
    // rather than starting narrow and waiting for the SPA's enter-split round
    // trip. enterSplit() no-ops safely now that drawerState === 'open'.
    if (startSplit) enterSplit();

    if (!discoverMounted) {
      let mountFailed = false;
      try {
        // Run the bundle load + handoff prep in parallel — the loading dots
        // cover both. SPA only mounts once both have resolved.
        const tasks = [loadGalleryWallESM()];
        if (typeof prepareHandoff === 'function') {
          tasks.push(prepareHandoff());
        }
        await Promise.all(tasks);

        if (window.initGalleryWall) {
          const gwApp = document.getElementById('dd-gw-app');
          const pdpContainer = document.querySelector('.whisper-block-container');
          const appUrl = pdpContainer?.dataset.appUrl || APP_PROXY_PATH;
          const market = pdpContainer?.dataset.market || 'gb';

          // Mount in compact + drawer-mounted mode. forceCompact = true
          // initially keeps the SPA's single-column chat layout while the
          // drawer is at its narrow width. drawerMounted = true keeps the
          // history-state guards on regardless of subsequent compact toggles
          // (the drawer can flip forceCompact via `discover:set-compact`
          // events without the SPA suddenly mutating the host page's URL).
          window.initGalleryWall({
            appUrl,
            market,
            mode: 'discover',
            forceCompact: true,
            drawerMounted: true,
          }, gwApp);

          const loading = document.getElementById('dd-loading');
          if (loading) loading.style.display = 'none';
          gwApp.style.display = '';
          discoverMounted = true;
        } else {
          mountFailed = true;
        }
      } catch (err) {
        console.error('[Whisper] Failed to load DiscoverApp:', err);
        mountFailed = true;
      }

      if (mountFailed) {
        // Replace the loading dots with a visible error so the user
        // doesn't sit in front of an empty drawer forever. Don't set
        // discoverMounted — the next open will retry the load.
        const loading = document.getElementById('dd-loading');
        if (loading) {
          loading.innerHTML = '<div style="text-align:center;padding:24px;color:#666;font-size:14px;">Couldn’t load the Art Advisor. Please try again.</div>';
        }
      }
    }

    setTimeout(() => {
      if (drawerEl) {
        drawerEl.classList.remove('dd-drawer--opening');
      }
      // Move keyboard focus into the drawer once the opening animation has
      // played — the SPA inside should grab focus to its input/first chip,
      // but the close button is the safe fallback so keyboard users always
      // have a route out.
      const closeBtn = drawerEl?.querySelector('.dd-close');
      if (closeBtn && !drawerEl.contains(document.activeElement)) {
        closeBtn.focus();
      }
    }, 300);
  }

  function closeDiscoverDrawer() {
    if (drawerState === 'closed') return;

    // Reset to narrow before the close animation runs so the next open
    // doesn't briefly flash the split layout. Tell the SPA to go back
    // to compact so isMobile / mobile chrome is restored. exitSplit()
    // also resyncs the toggle button label back to "Show wall →".
    exitSplit();

    drawerEl.classList.add('dd-drawer--closing');
    backdropEl.classList.remove('dd-backdrop--visible');
    // The backdrop is `position: fixed; inset: 0` — even when its
    // background fades to transparent it still intercepts clicks.
    // Disable pointer events immediately and hide it after the
    // animation, otherwise the host page becomes unclickable.
    backdropEl.style.pointerEvents = 'none';
    document.body.style.overflow = '';

    document.dispatchEvent(new CustomEvent('whisper:chat-closed', { bubbles: true }));

    setTimeout(() => {
      if (drawerEl) {
        drawerEl.style.display = 'none';
        drawerEl.className = 'dd-drawer';
      }
      if (backdropEl) {
        backdropEl.style.display = 'none';
      }
      drawerState = 'closed';
    }, 300);
  }

  // ── Chat bridge ───────────────────────────────────────────────────────
  //
  // Anything (whisper widget, PDP advisor callouts via pdp-surgical.js,
  // homepage hero, etc.) can dispatch `whisper:open-chat` with detail:
  //   { whisper?: { text, widget }, userAction, productHandle, focusedPiece, intent }
  // We stage the handoff in sessionStorage and open the drawer. DiscoverApp
  // reads the handoff on mount and engages the right skill (focusedPiece
  // routes to art_design / frame_design instead of wall_curation).

  // Upload a base64 dataUrl to S3 via the gallery-wall app's existing
  // /api/upload-photo endpoint. Returns { url, key, contentHash } or throws.
  async function uploadPhotoForHandoff(uploadedPhoto) {
    if (!uploadedPhoto?.dataUrl) return null;
    const baseUrl = getAppBaseUrl();
    const res = await fetch(`${baseUrl}/api/upload-photo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dataUrl: uploadedPhoto.dataUrl,
        filename: uploadedPhoto.fileName || 'photo',
      }),
    });
    if (!res.ok) {
      const err = await res.text().catch(() => res.statusText);
      throw new Error(`Photo upload failed (${res.status}): ${err}`);
    }
    return res.json();
  }

  document.addEventListener('whisper:open-chat', (e) => {
    const detail = e.detail || {};
    const { whisper, userAction, productHandle, focusedPiece, intent, uploadedPhoto, framingProductHandle } = detail;

    // The SPA reads + deletes whisper:handoff on mount. If the drawer is
    // already open it has already consumed any prior handoff; writing a
    // new one here would just create an orphan that triggers on the next
    // page load. Re-dispatch the same intent in-app instead — for now,
    // bail (mid-session context-switch is a future enhancement).
    if (drawerState !== 'closed') return;

    // ab12: if the user switched skills since the last mount (e.g. PDP
    // frame/size reco → gallery-wall curation), tear down the mounted
    // SPA so the next open starts a fresh conversation. The SPA's own
    // discover:replay-handoff path leaves prior chat content visible
    // for cross-skill transitions.
    //
    // Bucket by *skill*, not just focusedPiece presence — otherwise PDP
    // "Not sure what to pick" (focusedPiece + intent=general → frame_design)
    // and PDP CTW trio (focusedPiece + intent=trio → wall_curation) both
    // resolve to 'pdp' and the teardown never fires, leaving the prior
    // conversation visible on reopen.
    const WALL_INTENTS = new Set(['pair', 'trio', 'wall', 'curate', 'gallery']);
    const newIntentBucket = WALL_INTENTS.has(intent)
      ? 'wall'
      : (focusedPiece ? 'pdp' : 'gw');
    if (discoverMounted && lastIntentBucket && lastIntentBucket !== newIntentBucket) {
      const gwApp = document.getElementById('dd-gw-app');
      if (gwApp) {
        gwApp.innerHTML = '';
        gwApp.style.display = 'none';
      }
      const loading = document.getElementById('dd-loading');
      if (loading) loading.style.display = '';
      discoverMounted = false;
    }
    lastIntentBucket = newIntentBucket;

    // Build the conversation history once.
    const conversationHistory = [];
    if (whisper && whisper.text) {
      conversationHistory.push({ role: 'assistant', content: whisper.text, widget: whisper.widget });
    }

    // Pass-through any pre-uploaded photo (caller supplied url already).
    let resolvedPhoto = null;
    if (uploadedPhoto?.url) {
      resolvedPhoto = {
        url: uploadedPhoto.url,
        fileName: uploadedPhoto.fileName || null,
        width: uploadedPhoto.width || null,
        height: uploadedPhoto.height || null,
      };
    }

    function writeHandoff() {
      try {
        // Pass the original dataUrl through as a fallback when the S3 upload
        // hasn't resolved (or has failed). The SPA can render it inline as the
        // photo_slot source immediately, and a later writeHandoff call (after
        // upload succeeds) overwrites with the resolved URL for the next read.
        // Without this, the personalise SPA branch reads url=null on the
        // initial handoff and falls through to wall-curation, never mounting
        // the live preview.
        const handoffPhoto = resolvedPhoto || (uploadedPhoto?.dataUrl ? {
          dataUrl: uploadedPhoto.dataUrl,
          fileName: uploadedPhoto.fileName || null,
          width: uploadedPhoto.width || null,
          height: uploadedPhoto.height || null,
        } : null);
        sessionStorage.setItem('whisper:handoff', JSON.stringify({
          conversationHistory,
          userAction,
          productHandle,
          focusedPiece,
          intent,
          uploadedPhoto: handoffPhoto,
          // RMF "Let Advisor design your frame" entry — the frame product
          // is not the artwork (that's focusedPiece, now a synthetic upload
          // record), but the advisor still benefits from knowing which RM
          // range was the user's entry point: it's a soft preference for
          // recommendations, and it's the apply target when the user picks
          // a design within the same range. pdp-surgical.js sets this
          // alongside the upload-typed focusedPiece for the frame_my_photo
          // intent; null for every other entry point.
          framingProductHandle: framingProductHandle || null,
        }));
      } catch { /* ignore */ }
    }

    // openDiscoverDrawer awaits prepareHandoff() in parallel with the bundle
    // load and only mounts the SPA after both have resolved. That guarantees
    // the SPA reads the handoff exactly once, with the resolved photo URL.
    // Note: on a *second* open, discoverMounted is already true and this
    // callback is skipped — the replay path below handles re-entry.
    openDiscoverDrawer(async () => {
      if (uploadedPhoto?.dataUrl && !resolvedPhoto?.url) {
        try {
          const r = await uploadPhotoForHandoff(uploadedPhoto);
          resolvedPhoto = {
            url: r?.url || null,
            fileName: uploadedPhoto.fileName || null,
            width: uploadedPhoto.width || null,
            height: uploadedPhoto.height || null,
          };
        } catch (err) {
          console.error('[Whisper] Photo upload failed; opening advisor without photo:', err);
          resolvedPhoto = null;
        }
      }
      writeHandoff();
    }, intent === 'personalise' || intent === 'art_design');

    // Stage an initial handoff in case prepareHandoff fails or mount races —
    // the prepareHandoff path will overwrite this with the resolved URL.
    writeHandoff();

    // If the SPA was mounted on a previous open, the prepareHandoff callback
    // above is skipped (openDiscoverDrawer's mount block runs once). The SPA's
    // onMount handoff block has already fired and won't re-read sessionStorage
    // on its own. Optionally upload the photo, then tell the SPA to replay so
    // the new prompt starts a fresh conversation instead of silently dropping.
    if (discoverMounted) {
      (async () => {
        if (uploadedPhoto?.dataUrl && !resolvedPhoto?.url) {
          try {
            const r = await uploadPhotoForHandoff(uploadedPhoto);
            resolvedPhoto = {
              url: r?.url || null,
              fileName: uploadedPhoto.fileName || null,
              width: uploadedPhoto.width || null,
              height: uploadedPhoto.height || null,
            };
            writeHandoff();
          } catch (err) {
            console.error('[Whisper] Photo upload failed on re-open; replaying without photo:', err);
          }
        }
        document.dispatchEvent(new CustomEvent('discover:replay-handoff'));
      })();
    }
  });

  // ── Initialization ────────────────────────────────────────────────────

  function init() {
    // PDP whisper
    const pdpContainer = document.querySelector('.whisper-block-container');
    if (pdpContainer) {
      mountWhisper(pdpContainer);
    }

    // Cart whisper
    initCartWhisper();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
